(() => {
  'use strict';

  const CUSTOM_VALUE = '__custom__';
  const money = (cents, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD'
  }).format((Number(cents) || 0) / 100);

  const parseJson = (root, selector, fallback) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || ''); }
    catch (_) { return fallback; }
  };

  const setProperty = (root, name, value) => {
    const input = root.querySelector(`[data-srd-property="${name}"]`);
    if (input) input.value = value == null ? '' : String(value);
  };

  const normalizeCustom = (value) => {
    let count = 0;
    return Array.from(String(value || '').replace(/[\r\n]+/g, ' ')).filter((char) => {
      if (/\s/.test(char)) return true;
      count += 1;
      return count <= 30;
    }).join('').toUpperCase();
  };

  const variantKey = (size, material, lamination) => [size, material, lamination].join('|').toLowerCase();
  const pricingKey = (sizeId, materialId, laminationId) => [sizeId, materialId, laminationId].join('|').toLowerCase();

  function buildVariantMap(variants, optionNames) {
    const indexes = {
      size: optionNames.findIndex((name) => /size/i.test(name)),
      material: optionNames.findIndex((name) => /material/i.test(name)),
      lamination: optionNames.findIndex((name) => /lamination|finish/i.test(name))
    };
    if (indexes.size < 0) return new Map();
    return new Map(variants.map((variant) => [
      variantKey(
        variant.options[indexes.size],
        indexes.material >= 0 ? variant.options[indexes.material] : 'Standard Industrial Vinyl',
        indexes.lamination >= 0 ? variant.options[indexes.lamination] : 'None'
      ),
      variant
    ]));
  }

  function containSettledArtwork(root, expectedScale) {
    const legend = root.querySelector('[data-srd-preview-legend]');
    const preview = root.querySelector('[data-srd-preview]');
    const artwork = root.querySelector('[data-srd-preview-artwork]');
    if (!legend || !preview || !artwork || !Number.isFinite(expectedScale) || expectedScale <= 0) return;

    const previewRect = preview.getBoundingClientRect();
    const previewStyle = getComputedStyle(preview);
    const content = {
      left: previewRect.left + Number.parseFloat(previewStyle.borderLeftWidth) + Number.parseFloat(previewStyle.paddingLeft),
      right: previewRect.right - Number.parseFloat(previewStyle.borderRightWidth) - Number.parseFloat(previewStyle.paddingRight),
      top: previewRect.top + Number.parseFloat(previewStyle.borderTopWidth) + Number.parseFloat(previewStyle.paddingTop),
      bottom: previewRect.bottom - Number.parseFloat(previewStyle.borderBottomWidth) - Number.parseFloat(previewStyle.paddingBottom)
    };
    const textRange = document.createRange();
    textRange.selectNodeContents(legend);
    const visibleArrows = Array.from(preview.querySelectorAll('.srd-industrial-label__arrow'))
      .filter((arrow) => getComputedStyle(arrow).display !== 'none');
    const rectangles = [textRange.getBoundingClientRect(), ...visibleArrows.map((arrow) => arrow.getBoundingClientRect())]
      .filter((rect) => rect.width > 0 && rect.height > 0);
    if (!rectangles.length) return;

    const currentScale = Number.parseFloat(previewStyle.getPropertyValue('--srd-display-scale')) || expectedScale;
    const centerX = (content.left + content.right) / 2;
    const centerY = (content.top + content.bottom) / 2;
    const requiredDisplayWidth = Math.max(...rectangles.flatMap((rect) => [centerX - rect.left, rect.right - centerX])) * 2;
    const requiredDisplayHeight = Math.max(...rectangles.flatMap((rect) => [centerY - rect.top, rect.bottom - centerY])) * 2;
    const effectiveProductionWidth = Math.max(artwork.offsetWidth, requiredDisplayWidth / currentScale);
    const effectiveProductionHeight = Math.max(artwork.offsetHeight, requiredDisplayHeight / currentScale);
    const availableWidth = Math.max(1, content.right - content.left);
    const availableHeight = Math.max(1, content.bottom - content.top);
    const containedScale = Math.min(expectedScale, availableWidth / effectiveProductionWidth, availableHeight / effectiveProductionHeight);
    preview.style.setProperty('--srd-display-scale', String(containedScale));
  }

  function queueSettledContainment(root, expectedScale) {
    const preview = root.querySelector('[data-srd-preview]');
    if (!preview) return;
    const token = String((Number(preview.dataset.srdContainmentToken) || 0) + 1);
    preview.dataset.srdContainmentToken = token;
    let remainingFrames = 8;
    const settle = () => {
      if (preview.dataset.srdContainmentToken !== token) return;
      containSettledArtwork(root, expectedScale);
      remainingFrames -= 1;
      if (remainingFrames > 0) window.requestAnimationFrame(settle);
    };
    window.requestAnimationFrame(() => window.requestAnimationFrame(settle));
  }

  function fitLegend(root) {
    const legend = root.querySelector('[data-srd-preview-legend]');
    const preview = root.querySelector('[data-srd-preview]');
    const artwork = root.querySelector('[data-srd-preview-artwork]');
    if (!legend || !preview || !artwork) return;

    const unitsPerInch = 100;
    const requestedWidth = Number(preview.dataset.productionWidth) || 8;
    const requestedHeight = Number(preview.dataset.productionHeight) || 1;
    const compactFiveToOne = Math.abs(requestedWidth - 3.75) < .001 && Math.abs(requestedHeight - .75) < .001;
    const physicalWidth = Math.max(1, requestedWidth);
    const physicalHeight = compactFiveToOne ? requestedHeight : Math.max(1, requestedHeight);
    const productionWidth = physicalWidth * unitsPerInch;
    const productionHeight = physicalHeight * unitsPerInch;
    const referenceHeight = 100;
    const productionScale = productionHeight / referenceHeight;
    const designWidth = productionWidth / productionScale;
    const horizontalMargin = referenceHeight * (compactFiveToOne ? .05 : .12);
    const verticalMargin = referenceHeight * (compactFiveToOne ? .015 : .04);
    const printableWidth = Math.max(1, designWidth - horizontalMargin * 2);
    const printableHeight = referenceHeight - verticalMargin * 2;
    const arrowCount = preview.dataset.arrow === 'bidirectional' ? 2 : 1;
    const previewStyle = getComputedStyle(preview);
    const availableWidth = preview.clientWidth - Number.parseFloat(previewStyle.paddingLeft) - Number.parseFloat(previewStyle.paddingRight);
    const availableHeight = preview.clientHeight - Number.parseFloat(previewStyle.paddingTop) - Number.parseFloat(previewStyle.paddingBottom);
    const text = legend.textContent.trim() || ' ';
    const characterCount = text.replace(/\s/g, '').length;
    const optical = compactFiveToOne
      ? characterCount <= 3
        ? { name: 'very-short', height: .995, tracking: -.006, gap: .035 }
        : characterCount <= 10
          ? { name: 'short', height: .985, tracking: -.026, gap: .034 }
          : characterCount <= 15
            ? { name: 'medium', height: .965, tracking: -.058, gap: .032 }
            : { name: 'long', height: .925, tracking: -.052, gap: .03 }
      : characterCount <= 3
        ? { name: 'very-short', height: .995, tracking: -.006, gap: .05 }
        : characterCount <= 10
          ? { name: 'short', height: .965, tracking: -.014, gap: .047 }
          : characterCount <= 15
            ? { name: 'medium', height: .925, tracking: -.023, gap: .044 }
            : { name: 'long', height: .875, tracking: -.032, gap: .041 };
    const productionGap = printableHeight * optical.gap;
    const productionArrow = printableHeight * (compactFiveToOne ? .52 : .64);
    const canvas = fitLegend.canvas || (fitLegend.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    if (!context || availableWidth <= 0 || availableHeight <= 0) return;

    const metricsAt = (size) => {
      context.font = `900 ${size}px "SRD Archivo Black", sans-serif`;
      const measured = context.measureText(text);
      const left = Number.isFinite(measured.actualBoundingBoxLeft) ? measured.actualBoundingBoxLeft : 0;
      const right = Number.isFinite(measured.actualBoundingBoxRight) ? measured.actualBoundingBoxRight : measured.width;
      const ascent = Number.isFinite(measured.actualBoundingBoxAscent) ? measured.actualBoundingBoxAscent : size * .74;
      const descent = Number.isFinite(measured.actualBoundingBoxDescent) ? measured.actualBoundingBoxDescent : size * .18;
      const tracking = size * optical.tracking * Math.max(0, text.length - 1);
      const glyphHeight = ascent + descent;
      return {
        width: Math.max(1, left + right + tracking),
        height: Math.max(1, compactFiveToOne ? Math.max(glyphHeight, size * .77) : glyphHeight)
      };
    };

    const findMaximum = (high, includesArrows) => {
      let low = 8;
      for (let index = 0; index < 16; index += 1) {
        const middle = (low + high) / 2;
        const measured = metricsAt(middle);
        const compositionWidth = measured.width + (includesArrows ? arrowCount * (productionArrow + productionGap) : 0);
        if (measured.height <= printableHeight * optical.height && compositionWidth <= printableWidth) low = middle;
        else high = middle;
      }
      return low;
    };

    const maximumLegend = findMaximum(160, false);
    const composedLegend = findMaximum(maximumLegend, true);
    const fontSize = Math.max(8, composedLegend * .992);
    const artworkWidth = printableWidth * productionScale;
    const artworkHeight = printableHeight * productionScale;
    const displayScale = Math.min(availableWidth / artworkWidth, availableHeight / artworkHeight);

    if (compactFiveToOne) preview.dataset.productionProfile = 'compact-five-to-one';
    else delete preview.dataset.productionProfile;
    preview.dataset.opticalClass = optical.name;
    legend.style.setProperty('--srd-legend-tracking', `${optical.tracking}em`);
    artwork.style.width = `${artworkWidth}px`;
    artwork.style.height = `${artworkHeight}px`;
    preview.style.setProperty('--srd-display-scale', String(displayScale));
    preview.style.setProperty('--srd-production-gap', `${productionGap * productionScale}px`);
    preview.style.setProperty('--srd-production-font-size', `${fontSize * productionScale}px`);
    preview.style.setProperty('--srd-production-arrow-size', `${productionArrow * productionScale}px`);
    preview.style.setProperty('--srd-production-maximum-legend', String(maximumLegend * productionScale));
    containSettledArtwork(root, displayScale);
    queueSettledContainment(root, displayScale);
  }

  async function init(root) {
    if (root.dataset.srdInitialized === 'true') return;
    root.dataset.srdInitialized = 'true';

    const standards = await fetch(root.dataset.standardsUrl, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error('Industrial standards unavailable');
      return response.json();
    });
    const family = standards.product_families.pipe_conduit_labels;
    const presets = family.presets.filter((preset) => preset.active).sort((a, b) => a.display_order - b.display_order);
    const embeddedVariants = parseJson(root, '[data-srd-product-variants]', []);
    let variants = embeddedVariants;
    try {
      const liveProduct = await fetch(`${window.location.pathname}.js`, { credentials: 'same-origin', cache: 'no-store' }).then((response) => {
        if (!response.ok) throw new Error('Live product data unavailable');
        return response.json();
      });
      if (Array.isArray(liveProduct?.variants) && liveProduct.variants.length) variants = liveProduct.variants;
    } catch (_) {
      // Keep the server-rendered variant payload as a safe fallback.
    }
    const productOptions = parseJson(root, '[data-srd-product-options]', []);
    const optionNames = productOptions.map((option) => option.name);
    const variantsByKey = buildVariantMap(variants, optionNames);
    const pricingRecords = standards.pricing?.records || {};

    const elements = {
      search: root.querySelector('[data-srd-label-search]'),
      preset: root.querySelector('[data-srd-label-preset]'),
      result: root.querySelector('[data-srd-search-result]'),
      customField: root.querySelector('[data-srd-custom-field]'),
      customText: root.querySelector('[data-srd-custom-text]'),
      customCount: root.querySelector('[data-srd-custom-count]'),
      size: root.querySelector('[data-srd-size]'),
      material: root.querySelector('[data-srd-material]'),
      lamination: root.querySelector('[data-srd-lamination]'),
      standardColors: root.querySelector('[data-srd-standard-colors]'),
      customColors: root.querySelector('[data-srd-custom-colors]'),
      background: root.querySelector('[data-srd-background-color]'),
      text: root.querySelector('[data-srd-text-color]'),
      quantity: root.querySelector('[data-srd-quantity]'),
      quantityHelp: root.querySelector('[data-srd-quantity-help]'),
      variantId: root.querySelector('[data-srd-variant-id]'),
      cartQuantity: root.querySelector('[data-srd-cart-quantity]'),
      submit: root.querySelector('[data-srd-submit]'),
      error: root.querySelector('[data-srd-config-error]'),
      preview: root.querySelector('[data-srd-preview]'),
      previewLegend: root.querySelector('[data-srd-preview-legend]'),
      previewSize: root.querySelector('[data-srd-preview-size]'),
      livePrice: root.querySelector('[data-srd-live-price]'),
      unitPrice: root.querySelector('[data-srd-unit-price]')
    };

    const presetByLegend = new Map(presets.map((preset) => [preset.legend, preset]));
    const option = (value, label) => new Option(label, value);
    const storageKey = `srd:industrial-label-configurator:v1:${window.location.pathname}`;

    const loadPersistedState = () => {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
        if (!saved || typeof saved !== 'object') return null;
        if (saved.preset !== CUSTOM_VALUE && !presetByLegend.has(saved.preset)) return null;
        if (!standards.sizes[saved.size] || !standards.materials[saved.material] || !standards.laminations[saved.lamination]) return null;
        if (!standards.colors[saved.background] || !standards.colors[saved.text]) return null;
        return saved;
      } catch (_) {
        return null;
      }
    };

    const persistState = () => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify({
          schema_version: '1.0.0',
          preset: elements.preset.value,
          customText: elements.customText.value,
          size: elements.size.value,
          material: elements.material.value,
          lamination: elements.lamination.value,
          standardColors: elements.standardColors.checked,
          background: elements.background.value,
          text: elements.text.value,
          arrow: root.querySelector('[data-srd-arrow]:checked')?.value || 'right',
          quantity: elements.quantity.value
        }));
      } catch (_) { /* Defaults remain available when storage is unavailable. */ }
    };

    Object.entries(standards.sizes).forEach(([id, size]) => elements.size.add(option(id, size.label)));
    Object.entries(standards.materials).forEach(([id, material]) => elements.material.add(option(id, material.label)));
    Object.entries(standards.laminations).forEach(([id, lamination]) => elements.lamination.add(option(id, lamination.label)));
    Object.entries(standards.colors).forEach(([id, color]) => {
      elements.background.add(option(id, color.label));
      elements.text.add(option(id, color.label));
    });

    const renderPresetOptions = (filter = '') => {
      const selected = elements.preset.value;
      const query = filter.trim().toLowerCase();
      const matches = presets.filter((preset) => !query || `${preset.legend} ${preset.category}`.toLowerCase().includes(query));
      elements.preset.replaceChildren(...matches.map((preset) => option(preset.legend, `${preset.legend} — ${preset.category}`)), option(CUSTOM_VALUE, 'CUSTOM LABEL...'));
      if (matches.some((preset) => preset.legend === selected) || selected === CUSTOM_VALUE) elements.preset.value = selected;
      elements.result.textContent = query ? `${matches.length} preset${matches.length === 1 ? '' : 's'} found. Custom label remains available.` : `${matches.length} industrial presets available.`;
      if (!elements.preset.value) elements.preset.value = matches[0]?.legend || CUSTOM_VALUE;
    };

    const currentPreset = () => presetByLegend.get(elements.preset.value) || presets[0];
    const currentArrow = () => root.querySelector('[data-srd-arrow]:checked')?.value || 'right';
    const colorValue = (id) => standards.colors[id] || standards.colors.black;
    let activeSizeId = elements.size.value;

    const update = () => {
      const isCustom = elements.preset.value === CUSTOM_VALUE;
      const preset = currentPreset();
      const customLegend = normalizeCustom(elements.customText.value);
      if (elements.customText.value !== customLegend) elements.customText.value = customLegend;
      elements.customCount.textContent = String(customLegend.replace(/\s/g, '').length);
      elements.customField.hidden = !isCustom;

      const legend = isCustom ? customLegend : preset.legend;
      const arrow = currentArrow();
      const size = standards.sizes[elements.size.value];
      const sizeChanged = elements.size.value !== activeSizeId;
      activeSizeId = elements.size.value;
      const material = standards.materials[elements.material.value];
      const lamination = standards.laminations[elements.lamination.value];
      const backgroundId = elements.standardColors.checked ? preset.background_color : elements.background.value;
      const textId = elements.standardColors.checked ? preset.text_color : elements.text.value;
      const background = colorValue(backgroundId);
      const text = colorValue(textId);

      elements.customColors.hidden = elements.standardColors.checked;
      elements.background.disabled = elements.standardColors.checked;
      elements.text.disabled = elements.standardColors.checked;
      if (elements.standardColors.checked) {
        elements.background.value = preset.background_color;
        elements.text.value = preset.text_color;
      }

      const min = size.minimum_quantity;
      const step = size.quantity_increment;
      let quantity = sizeChanged ? min : Number.parseInt(elements.quantity.value, 10);
      if (!Number.isFinite(quantity) || quantity < min) quantity = min;
      quantity = min + Math.round((quantity - min) / step) * step;
      elements.quantity.min = String(min);
      elements.quantity.step = String(step);
      elements.quantity.value = String(quantity);
      elements.cartQuantity.value = String(quantity);
      const priceRecord = pricingRecords[pricingKey(elements.size.value, elements.material.value, elements.lamination.value)] || null;
      const baseQuantity = Number(priceRecord?.base_quantity || 0);
      const basePriceCents = Number(priceRecord?.base_price_cents || 0);
      const approvedPrice = priceRecord?.status === 'approved' && baseQuantity > 0 && basePriceCents > 0;
      const packageCount = approvedPrice ? quantity / baseQuantity : 0;
      elements.quantityHelp.textContent = approvedPrice
        ? `Sold in manufacturing packages of ${baseQuantity}. Minimum order: ${min} labels.`
        : `This configuration is ready for manufacturing data; approved retail pricing is pending.`;

      const key = variantKey(size.label, material.label, lamination.label);
      const variant = variantsByKey.get(key) || null;
      const shopifyPriceCents = Number(variant?.price || 0);
      const priceMatchesShopify = approvedPrice && shopifyPriceCents === basePriceCents;
      const canOrder = approvedPrice && Number.isInteger(packageCount) && packageCount > 0 && priceMatchesShopify && Boolean(variant?.available) && Boolean(legend);
      const availabilityMessage = !approvedPrice
        ? 'Approved retail pricing is pending for this size, material, and lamination combination.'
        : !variant
          ? 'The matching Shopify variant has not been configured. Ordering is disabled until product setup is complete.'
          : !priceMatchesShopify
            ? 'The approved standards price does not match the Shopify variant price. Ordering is disabled until product data is corrected.'
            : !variant.available
              ? 'This approved configuration is currently unavailable.'
              : !legend
                ? 'Enter custom label text before adding this configuration to the cart.'
                : '';
      elements.error.hidden = !availabilityMessage;
      elements.error.textContent = availabilityMessage;
      elements.submit.disabled = !canOrder;
      if (variant) elements.variantId.value = String(variant.id);
      elements.cartQuantity.value = canOrder ? String(packageCount) : '1';

      const currency = standards.pricing?.currency || window.Shopify?.currency?.active || 'USD';
      const totalCents = approvedPrice ? basePriceCents * packageCount : 0;
      elements.livePrice.textContent = approvedPrice ? money(totalCents, currency) : 'Pricing pending';
      elements.unitPrice.textContent = approvedPrice
        ? `${money(basePriceCents, currency)} per ${baseQuantity}-label manufacturing package`
        : 'No placeholder price assigned';

      elements.preview.style.aspectRatio = `${size.width} / ${size.height}`;
      elements.preview.style.backgroundColor = background.hex;
      elements.preview.style.color = text.hex;
      elements.preview.dataset.arrow = arrow;
      elements.preview.dataset.productionWidth = String(size.width);
      elements.preview.dataset.productionHeight = String(size.height);
      elements.previewLegend.textContent = legend || 'ENTER CUSTOM TEXT';
      elements.previewSize.textContent = size.label;

      const summary = { legend: legend || 'Custom text required', size: size.label, material: material.label, lamination: lamination.label };
      Object.entries(summary).forEach(([name, value]) => {
        const target = root.querySelector(`[data-srd-summary="${name}"]`);
        if (target) target.textContent = value;
      });

      const pricingResult = approvedPrice
        ? `${money(basePriceCents, currency)} per ${baseQuantity}-label package / ${money(totalCents, currency)} total`
        : 'Approved retail pricing pending';
      const configuration = {
        schema_version: '1.0.0', product_family: 'pipe_conduit_labels', preset: isCustom ? 'CUSTOM LABEL...' : preset.legend,
        custom_text: isCustom ? customLegend : '', legend, background_color: background.label, background_hex: background.hex,
        text_color: text.label, text_hex: text.hex, use_industry_standard_colors: elements.standardColors.checked,
        arrow_direction: arrow, size_id: elements.size.value, label_size: size.label, width: size.width, height: size.height,
        material: material.label, lamination: lamination.label, quantity, pricing_group: size.pricing_group,
        shipping_group: size.shipping_group, pricing_status: approvedPrice ? 'approved' : 'pending', pricing_basis: standards.pricing?.basis || null,
        base_quantity: approvedPrice ? baseQuantity : null, manufacturing_packages: approvedPrice ? packageCount : null,
        variant_id: variant?.id || null, base_price_cents: approvedPrice ? basePriceCents : null, total_price_cents: approvedPrice ? totalCents : null
      };
      setProperty(root, 'Label', isCustom ? '' : configuration.legend);
      setProperty(root, 'Custom Text', configuration.custom_text);
      setProperty(root, '_Background HEX', background.hex);
      setProperty(root, '_Text HEX', text.hex);
      setProperty(root, 'Arrow', arrow.charAt(0).toUpperCase() + arrow.slice(1));
      setProperty(root, '_Size', size.label);
      setProperty(root, '_Material', material.label);
      setProperty(root, '_Lamination', lamination.label);
      setProperty(root, '_Pricing Result', pricingResult);
      setProperty(root, '_Configuration JSON', JSON.stringify(configuration));
      persistState();
      window.requestAnimationFrame(() => fitLegend(root));
    };

    renderPresetOptions();
    const persistedState = loadPersistedState();
    if (persistedState) {
      elements.preset.value = persistedState.preset;
      elements.customText.value = normalizeCustom(persistedState.customText);
      elements.size.value = persistedState.size;
      elements.material.value = persistedState.material;
      elements.lamination.value = persistedState.lamination;
      elements.standardColors.checked = persistedState.standardColors !== false;
      elements.background.value = persistedState.background;
      elements.text.value = persistedState.text;
      elements.quantity.value = String(persistedState.quantity || '');
      const persistedArrow = root.querySelector(`[data-srd-arrow][value="${persistedState.arrow}"]`);
      if (persistedArrow) persistedArrow.checked = true;
    } else {
      elements.preset.value = presets[0].legend;
    }
    activeSizeId = elements.size.value;
    elements.search.addEventListener('input', () => { renderPresetOptions(elements.search.value); update(); });
    elements.customText.addEventListener('input', update);
    [elements.preset, elements.size, elements.material, elements.lamination, elements.background, elements.text, elements.standardColors]
      .forEach((control) => control.addEventListener('change', () => {
        if (control === elements.preset && elements.preset.value !== CUSTOM_VALUE) {
          const preset = currentPreset();
          root.querySelector(`[data-srd-arrow][value="${preset.default_arrow}"]`).checked = true;
        }
        update();
      }));
    root.querySelectorAll('[data-srd-arrow]').forEach((radio) => radio.addEventListener('change', update));
    elements.quantity.addEventListener('change', update);
    root.querySelector('[data-srd-quantity-minus]').addEventListener('click', () => { elements.quantity.value = String(Number(elements.quantity.value) - Number(elements.quantity.step)); update(); });
    root.querySelector('[data-srd-quantity-plus]').addEventListener('click', () => { elements.quantity.value = String(Number(elements.quantity.value) + Number(elements.quantity.step)); update(); });
    window.addEventListener('resize', () => fitLegend(root), { passive: true });
    if ('ResizeObserver' in window) {
      const previewObserver = new ResizeObserver(() => fitLegend(root));
      previewObserver.observe(elements.preview);
    }
    if (document.fonts) {
      const refitWithSettledProductionFont = () => fitLegend(root);
      document.fonts.load('900 160px "SRD Archivo Black"', 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789')
        .then(() => document.fonts.ready)
        .then(refitWithSettledProductionFont, refitWithSettledProductionFont);
      document.fonts.addEventListener?.('loadingdone', refitWithSettledProductionFont);
    }
    if (document.readyState === 'complete') fitLegend(root);
    else window.addEventListener('load', () => fitLegend(root), { once: true });
    update();
  }

  const start = () => document.querySelectorAll('[data-srd-industrial-label]').forEach((root) => init(root).catch((error) => {
    root.querySelector('[data-srd-config-error]')?.removeAttribute('hidden');
    const submit = root.querySelector('[data-srd-submit]');
    if (submit) submit.disabled = true;
    console.error('SRD industrial label configurator:', error);
  }));

  document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', start);
})();
